import { Link } from '@tanstack/react-router';
import { cls } from '@/shared/lib/classes';
import type { ItemDeTela, SecaoDoMenu as Secao, Submenu } from '@/shared/lib/cotas';
import type { MenuPersonalizado } from './useMenuPersonalizado';

/**
 * Uma seção do menu — título, painel e submenus — e o menu de Favoritos
 * (`.spec/015`, decisões 75 e 105).
 *
 * Saiu do `AppShell` com a chegada dos submenus: três níveis de lista, cada um
 * com o seu recuo e o seu estado de aberto, no corpo do shell, estourariam o
 * limite de complexidade — e o shell deixaria de ser o que o comentário dele
 * diz que é, a moldura.
 */

/** Seta: aponta para a direita fechada, para baixo aberta. */
export function IconeSeta({ aberto }: { aberto: boolean }): React.ReactElement {
  return (
    <svg
      className={cls('h-3 w-3 shrink-0 transition-transform', aberto && 'rotate-90')}
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
    >
      <path d="M4.5 2.5L8 6l-3.5 3.5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function IconeEstrela({ cheia }: { cheia: boolean }): React.ReactElement {
  return (
    <svg className="h-4 w-4" viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M8 1.8l1.9 3.9 4.3.6-3.1 3 .7 4.3L8 11.6l-3.8 2 .7-4.3-3.1-3 4.3-.6z"
        fill={cheia ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconeSubirDescer({ direcao }: { direcao: 'subir' | 'descer' }): React.ReactElement {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d={direcao === 'subir' ? 'M2.5 7.5L6 4l3.5 3.5' : 'M2.5 4.5L6 8l3.5-3.5'}
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

/**
 * O ícone da natureza da tela, à esquerda do favorito.
 *
 * Só no menu de Favoritos: ali as telas de todos os módulos se misturam, e
 * ele é o que ainda diz se a tela é de lançar, de manter ou de ler — dentro do
 * submenu o título já diz. Os três
 * desenhos são de traço, no mesmo peso das outras marcas do menu:
 * fluxograma (duas caixas ligadas por seta) para processo, ficha para cadastro e folha com barras para
 * relatório.
 */
const ROTULO_DA_NATUREZA = {
  processo: 'Processo',
  cadastro: 'Cadastro',
  relatorio: 'Relatório',
} as const;

function IconeDaNatureza({
  natureza,
}: {
  natureza: keyof typeof ROTULO_DA_NATUREZA;
}): React.ReactElement {
  const desenho = {
    processo: (
      <>
        <rect x="1.8" y="2" width="5" height="4" rx=".6" />
        <rect x="9.2" y="10" width="5" height="4" rx=".6" />
        <path d="M4.3 6v4h3.4M6.3 8.6l1.4 1.4-1.4 1.4" />
      </>
    ),
    cadastro: (
      <>
        <rect x="2.5" y="3" width="11" height="10" rx="1" />
        <circle cx="6" cy="7" r="1.4" />
        <path d="M4 10.8c.4-1.1 1.1-1.6 2-1.6s1.6.5 2 1.6M9.5 6.5H12M9.5 9H12" />
      </>
    ),
    relatorio: (
      <>
        <path d="M4 1.8h5.5L12.5 5v9.2H4z" />
        <path d="M6.3 12V9.5M8.3 12V7.5M10.3 12v-3" />
      </>
    ),
  }[natureza];

  return (
    <span title={ROTULO_DA_NATUREZA[natureza]} className="grid w-4 shrink-0 place-items-center">
      <svg
        className="h-4 w-4 text-grafite-400"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {desenho}
      </svg>
      <span className="sr-only">{ROTULO_DA_NATUREZA[natureza]}:</span>
    </span>
  );
}

/** Botão pequeno do modo de organizar. Alvo de toque cheio no celular. */
const CLASSES_BOTAO_ICONE =
  'grid h-11 w-9 shrink-0 place-items-center text-grafite-400 transition-colors hover:text-papel-50 disabled:opacity-30 disabled:hover:text-grafite-400 lg:h-8 lg:w-7';

/**
 * Recuo do rótulo por nível — cada nível entra em relação ao título acima.
 *
 * O título da seção fica em 32px: `px-3` (12px) + a seta (12px) + `gap-2`
 * (8px). A tela da seção fica em 46px — `pl-11` (44px) mais os 2px da borda —,
 * um passo claro à direita. Com o mesmo recuo das telas, o título do submenu
 * põe a **seta** alinhada a elas e o texto em 64px; a tela dentro dele vai
 * para 66px (`pl-16` + a borda), sob o texto do submenu.
 *
 * A borda de tela ativa continua colada na lateral do menu em todos os
 * níveis: ela marca a linha inteira, e recuá-la junto com o texto a
 * transformaria num segundo fio no meio da coluna.
 */
const RECUO = {
  tela: 'pl-11',
  // No menu de Favoritos a tela leva o ícone **dentro** do recuo: 24px + o
  // ícone (16px) + o vão (4px) põem o texto nos mesmos 46px das outras telas,
  // e a coluna de rótulos continua alinhada com ou sem ícone.
  favorito: 'pl-6',
  submenu: 'pl-11',
  telaDoSubmenu: 'pl-16',
} as const;

interface PropsItem {
  readonly item: ItemDeTela;
  readonly recuo: string;
  readonly ativo: boolean;
  readonly aoNavegar: () => void;
  readonly organizando: boolean;
  /** A tela está nos favoritos — a estrela sai cheia. */
  readonly favorito: boolean;
  /** Só no menu de Favoritos: onde ela está na lista, para desligar a seta da ponta. */
  readonly posicao?: { primeiro: boolean; ultimo: boolean };
  readonly menu: MenuPersonalizado;
}

function BotoesDeOrganizar({
  item,
  favorito,
  posicao,
  menu,
}: Pick<PropsItem, 'item' | 'favorito' | 'posicao' | 'menu'>): React.ReactElement {
  return (
    <div className="flex shrink-0 items-center pr-1">
      {posicao ? (
        <>
          <button
            type="button"
            onClick={() => {
              menu.mover(item.cota, 'subir');
            }}
            disabled={posicao.primeiro}
            aria-label={`Subir ${item.rotulo}`}
            className={CLASSES_BOTAO_ICONE}
          >
            <IconeSubirDescer direcao="subir" />
          </button>
          <button
            type="button"
            onClick={() => {
              menu.mover(item.cota, 'descer');
            }}
            disabled={posicao.ultimo}
            aria-label={`Descer ${item.rotulo}`}
            className={CLASSES_BOTAO_ICONE}
          >
            <IconeSubirDescer direcao="descer" />
          </button>
        </>
      ) : null}
      <button
        type="button"
        onClick={() => {
          menu.alternarFavorito(item.cota);
        }}
        aria-pressed={favorito}
        aria-label={favorito ? `Tirar ${item.rotulo} dos favoritos` : `Favoritar ${item.rotulo}`}
        className={cls(CLASSES_BOTAO_ICONE, favorito && 'text-zarcao-500')}
      >
        <IconeEstrela cheia={favorito} />
      </button>
    </div>
  );
}

/**
 * Uma tela do menu.
 *
 * No modo de organizar, a linha ganha a estrela e, nos favoritos, as setas.
 * Os botões ficam **fora** do `<Link>`: um botão dentro de um link navegaria
 * junto com o clique, e quem só queria marcar a tela sairia da que está.
 */
function ItemDoMenu({
  item,
  recuo,
  ativo,
  aoNavegar,
  organizando,
  favorito,
  posicao,
  menu,
}: PropsItem): React.ReactElement {
  return (
    <div
      className={cls(
        'flex items-center border-l-2',
        ativo ? 'border-l-zarcao-500 bg-grafite-800' : 'border-l-transparent hover:bg-grafite-800',
      )}
    >
      <Link
        to={item.para}
        onClick={aoNavegar}
        className={cls(
          /*
            14px, peso normal, e a tela aberta em negrito — a escala do print
            que o Adriano mandou como modelo (decisão 104, último ajuste). Os
            16px de antes deixavam o rótulo longo em duas linhas e a coluna
            pesada; o título do módulo lê como título pela caixa alta e pelo
            negrito, e não pelo tamanho.

            O favorito tinha um ponto a mais (decisão 75), para o olho parar
            nele no meio da seção. Com o menu próprio (decisão 105) ele não
            divide lista com ninguém, e o ponto saiu.
          */
          'flex min-h-toque min-w-0 flex-1 items-center gap-1 pr-3 text-sm transition-colors',
          recuo,
          ativo ? 'font-semibold text-papel-50' : 'text-grafite-300 hover:text-papel-100',
        )}
      >
        {posicao && item.natureza !== 'painel' ? (
          <IconeDaNatureza natureza={item.natureza} />
        ) : null}
        {/*
          Quebra a linha em vez de cortar com reticências. Com o submenu, a tela
          entra mais um nível, e "Gerar pedidos das listas" virava "Gerar
          pedidos da…" — o fim do rótulo é justamente o que distingue esta tela
          da vizinha. Alargar a coluna foi descartado: ela disputa largura com
          as listagens, e o ganho sumiria no próximo rótulo mais longo.
        */}
        <span className="min-w-0 break-words py-2 leading-snug">{item.rotulo}</span>
      </Link>
      {organizando && item.natureza !== 'painel' ? (
        <BotoesDeOrganizar item={item} favorito={favorito} posicao={posicao} menu={menu} />
      ) : null}
    </div>
  );
}

interface PropsSecao {
  readonly secao: Secao;
  /** Índice da seção — vira o `id` dos itens, sem acento no seletor. */
  readonly indice: number;
  readonly aberta: boolean;
  readonly aoAlternar: () => void;
  readonly cotaAberta: string | null;
  readonly aoNavegar: () => void;
  readonly menu: MenuPersonalizado;
}

/** O traço de zarcão: a seção ou o submenu fechado guarda a tela aberta. */
function TracoDaTelaAberta(): React.ReactElement {
  return <span aria-hidden="true" className="ml-auto h-1.5 w-4 bg-zarcao-500" />;
}

/**
 * O trilho e o ramo de uma tela do submenu — "├──" e, na última, "└──"
 * (`.spec/015`, decisão 106).
 *
 * Sem eles, Processos, Cadastros e Relatórios eram três títulos cinzas soltos
 * entre telas do mesmo recuo, e não se via onde um submenu acabava e o outro
 * começava. O trilho desce da seta do submenu e o ramo liga cada tela a ele; o
 * da última para no meio da linha, e é o fim do trilho que fecha o grupo.
 *
 * O trilho fica a 50px da borda: a seta do submenu ocupa 44–56px (`pl-11` e
 * 12px de largura), e o trilho sai do centro dela. O ramo vai até 60px, a 6px
 * do texto (66px — `pl-16` mais a borda de 2px). Desenhado por cima da linha,
 * e sem receber clique, para o fundo de foco e de tela ativa não o apagar.
 */
function RamoDoSubmenu({ ultimo }: { ultimo: boolean }): React.ReactElement {
  return (
    <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 z-10">
      <span
        className={cls(
          'absolute left-[50px] top-0 w-px bg-grafite-600',
          ultimo ? 'h-1/2' : 'h-full',
        )}
      />
      <span className="absolute left-[50px] top-1/2 h-px w-2.5 bg-grafite-600" />
    </span>
  );
}

function SubmenuDoMenu({
  secao,
  submenu,
  idDosItens,
  cotaAberta,
  aoNavegar,
  menu,
}: {
  secao: Secao;
  submenu: Submenu;
  idDosItens: string;
  cotaAberta: string | null;
  aoNavegar: () => void;
  menu: MenuPersonalizado;
}): React.ReactElement {
  const aberto = menu.submenuAberto(secao, submenu);
  const contemAtiva = submenu.itens.some((item) => item.cota === cotaAberta);

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => {
          menu.alternarSubmenu(secao, submenu);
        }}
        aria-expanded={aberto}
        aria-controls={idDosItens}
        className={cls(
          'flex min-h-toque w-full items-center gap-2 pr-3 text-left text-[0.6875rem] font-medium uppercase tracking-wide text-grafite-500 transition-colors hover:text-papel-100 lg:min-h-0 lg:py-1.5',
          RECUO.submenu,
        )}
      >
        <IconeSeta aberto={aberto} />
        <span>{submenu.titulo}</span>
        {contemAtiva && !aberto ? <TracoDaTelaAberta /> : null}
      </button>

      {aberto ? (
        <div id={idDosItens} className="flex flex-col">
          {submenu.itens.map((item, posicao) => (
            <div key={item.cota} className="relative">
              <RamoDoSubmenu ultimo={posicao === submenu.itens.length - 1} />
              <ItemDoMenu
                item={item}
                recuo={RECUO.telaDoSubmenu}
                ativo={item.cota === cotaAberta}
                aoNavegar={aoNavegar}
                organizando={menu.organizando}
                favorito={menu.favoritos.includes(item.cota)}
                menu={menu}
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * O corpo da seção aberta, de cima para baixo: painel e o resto.
 *
 * Os favoritos moravam aqui, logo abaixo do painel, cada um na seção do seu
 * módulo. Desde a decisão 105 eles têm menu próprio (`MenuDeFavoritos`), e a
 * tela favorita continua no lugar dela, com a estrela cheia no modo de
 * organizar.
 */
function CorpoDaSecao({
  secao,
  indice,
  cotaAberta,
  aoNavegar,
  menu,
}: Omit<PropsSecao, 'aberta' | 'aoAlternar'>): React.ReactElement {
  const itemSimples = (item: ItemDeTela): React.ReactElement => (
    <ItemDoMenu
      key={item.cota}
      item={item}
      recuo={RECUO.tela}
      ativo={item.cota === cotaAberta}
      aoNavegar={aoNavegar}
      organizando={menu.organizando}
      favorito={menu.favoritos.includes(item.cota)}
      menu={menu}
    />
  );

  return (
    <div id={`modulo-${String(indice)}`} className="flex flex-col">
      {secao.paineis.map(itemSimples)}
      {secao.demais.map(itemSimples)}

      {secao.submenus.map((submenu, posicao) => (
        <SubmenuDoMenu
          key={submenu.titulo}
          secao={secao}
          submenu={submenu}
          idDosItens={`modulo-${String(indice)}-${String(posicao)}`}
          cotaAberta={cotaAberta}
          aoNavegar={aoNavegar}
          menu={menu}
        />
      ))}
    </div>
  );
}

/** O título que abre e fecha uma seção — de módulo ou a de Favoritos. */
function TituloDaSecao({
  titulo,
  aberta,
  aoAlternar,
  idDoCorpo,
  contemAtiva,
}: {
  titulo: string;
  aberta: boolean;
  aoAlternar: () => void;
  idDoCorpo: string;
  contemAtiva: boolean;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={aoAlternar}
      aria-expanded={aberta}
      aria-controls={idDoCorpo}
      className="flex min-h-toque w-full items-center gap-2 px-3 text-left text-grafite-400 transition-colors hover:text-papel-100 lg:min-h-0 lg:py-2"
    >
      <IconeSeta aberto={aberta} />
      {/* 11px, negrito, caixa alta e espaçado — o título do print-modelo
          (decisão 104). Na fonte do texto, e não na mono da `cota`: a mono em
          caixa alta lia como código, e não como nome de seção. */}
      <span className="text-[0.6875rem] font-bold uppercase tracking-wider">{titulo}</span>
      {/* Sem o traço, fechar a seção apagaria a única pista de onde o
          usuário está. */}
      {contemAtiva && !aberta ? <TracoDaTelaAberta /> : null}
    </button>
  );
}

/**
 * O menu de Favoritos: as telas marcadas de **todos** os módulos, numa lista
 * só, acima das seções (`.spec/015`, decisão 105).
 *
 * Substitui o bloco de favoritos no topo de cada seção: quem marca "Pedidos de
 * compra" e "Contas a pagar" quer as duas a um clique, e não abrir Compras e
 * Pagamento para achá-las. A ordem é a do vetor gravado, e as setas do modo de
 * organizar a mudam.
 *
 * **Some quando está vazio**, fora do modo de organizar: uma seção que não
 * leva a lugar nenhum seria a primeira linha do menu de quem nunca marcou
 * nada. Organizando, ela aparece com a instrução — é ali que a primeira
 * estrela vai cair.
 */
export function MenuDeFavoritos({
  itens,
  cotaAberta,
  aoNavegar,
  menu,
}: {
  itens: readonly ItemDeTela[];
  cotaAberta: string | null;
  aoNavegar: () => void;
  menu: MenuPersonalizado;
}): React.ReactElement | null {
  if (itens.length === 0 && !menu.organizando) return null;

  const aberta = menu.favoritosAbertos;

  return (
    <div className="flex flex-col">
      <TituloDaSecao
        titulo="Favoritos"
        aberta={aberta}
        aoAlternar={menu.alternarFavoritos}
        idDoCorpo="modulo-favoritos"
        contemAtiva={itens.some((item) => item.cota === cotaAberta)}
      />

      {aberta ? (
        <div id="modulo-favoritos" className="flex flex-col">
          {itens.length === 0 ? (
            <p className={cls('pr-3 text-xs leading-snug text-grafite-400', RECUO.tela)}>
              Marque a estrela de uma tela, em qualquer módulo, para ela aparecer aqui.
            </p>
          ) : null}
          {itens.map((item, posicao) => (
            <ItemDoMenu
              key={item.cota}
              item={item}
              recuo={RECUO.favorito}
              ativo={item.cota === cotaAberta}
              aoNavegar={aoNavegar}
              organizando={menu.organizando}
              favorito
              posicao={{ primeiro: posicao === 0, ultimo: posicao === itens.length - 1 }}
              menu={menu}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function SecaoDoMenu({
  secao,
  indice,
  aberta,
  aoAlternar,
  cotaAberta,
  aoNavegar,
  menu,
}: PropsSecao): React.ReactElement {
  const contemAtiva = secao.itens.some((item) => item.cota === cotaAberta);

  return (
    <div className="flex flex-col">
      <TituloDaSecao
        titulo={secao.titulo}
        aberta={aberta}
        aoAlternar={aoAlternar}
        idDoCorpo={`modulo-${String(indice)}`}
        contemAtiva={contemAtiva}
      />

      {aberta ? (
        <CorpoDaSecao
          secao={secao}
          indice={indice}
          cotaAberta={cotaAberta}
          aoNavegar={aoNavegar}
          menu={menu}
        />
      ) : null}
    </div>
  );
}
